import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import PageMeta from "@/components/seo/PageMeta";
import { FAQSchema } from "@/components/seo/schemas";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { CheckCircle2, ArrowRight, Quote } from "lucide-react";

const HOMEPAGE_FAQS = [
  {
    question: "Do directors need a technical background?",
    answer: "No. BoardSight is built for oversight and governance—no coding required."
  },
  {
    question: "Is this for management teams too?",
    answer: "BoardSight is designed for boards and board-facing leaders. We can include management in advisory engagements where alignment is essential."
  },
  {
    question: "Is this legal advice?",
    answer: "No. BoardSight provides governance education and advisory support. Involve counsel for jurisdiction-specific legal interpretation."
  }
];

const OUTCOMES = [
  "A defined AI oversight model (roles, committees, cadence)",
  "A board‑level AI risk view (taxonomy, thresholds, escalation)",
  "A management reporting format the board can rely on",
  "An incident escalation and materiality approach",
  "A reusable Oversight Toolkit (templates + checklists)"
];

const TESTIMONIALS = [
  {
    quote: "BoardSight helped us turn AI governance from abstract to operational.",
    attribution: "Board Chair"
  },
  {
    quote: "Finally: AI oversight in language directors can act on.",
    attribution: "Audit Committee Member"
  },
  {
    quote: "Clear, practical, and aligned to our risk framework.",
    attribution: "General Counsel"
  }
];

const Index: React.FC = () => {
  return (
    <Layout>
      <PageMeta
        title="BoardSight | Defensible AI Oversight for Directors"
        description="BoardSight helps boards operationalize AI governance with director training, board advisory, and a 12‑month subscription for ongoing oversight support."
        canonicalUrl="https://boardsight.ai"
      />
      <FAQSchema faqs={HOMEPAGE_FAQS} />

      {/* Hero Section */}
      <section className="pt-16 pb-20 md:pt-24 md:pb-28 bg-gradient-to-b from-muted/50 to-background">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6">
              Defensible AI oversight—built for directors.
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
              BoardSight helps boards move from AI uncertainty to a credible oversight model—so you can govern AI risk, meet rising expectations, and respond confidently to regulators, investors, and auditors.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="/contact#briefing">
                  Book a 25‑Minute Board AI Oversight Briefing
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                <Link to="/quiz">
                  Take the Assessment
                </Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              Designed for directors, committee chairs, and board‑facing leaders in legal, risk, compliance, and security.
            </p>
          </div>
        </div>
      </section>

      {/* Why Now Section */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-6">
              AI is now a board-level responsibility.
            </h2>
            <p className="text-lg text-muted-foreground">
              AI changes how organizations operate—and how risk manifests. Boards are increasingly expected to demonstrate clear oversight of AI risk ownership, governance structure, controls and reporting, incident readiness, and regulatory preparedness.
            </p>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Choose the level of support your board needs.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {/* Advisory Card */}
            <Card className="bg-card border-border hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-foreground">
                  Advisory for Boards
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Custom training and advisory to operationalize board AI governance: oversight models, reporting expectations, charters, incident escalation, and executive alignment.
                </p>
                <Button asChild variant="link" className="p-0 text-primary hover:text-primary/80">
                  <Link to="/advisory" className="inline-flex items-center gap-2">
                    Explore Advisory <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Intensive Card */}
            <Card className="bg-card border-border hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-foreground">
                  Director AI Oversight Intensive (30‑Day Sprint)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  A structured program that equips directors with a practical oversight framework, board‑ready templates, and the questions boards should be asking.
                </p>
                <Button asChild variant="link" className="p-0 text-primary hover:text-primary/80">
                  <Link to="/blueprint" className="inline-flex items-center gap-2">
                    View the Intensive <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>

            {/* Subscription Card */}
            <Card className="bg-card border-border hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="text-xl font-bold text-foreground">
                  Governance Subscription (12 months)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-muted-foreground">
                  Ongoing updates, template refreshes, quarterly briefings, and advisory access—so oversight stays current as AI and regulation evolve.
                </p>
                <Button asChild variant="link" className="p-0 text-primary hover:text-primary/80">
                  <Link to="/subscription" className="inline-flex items-center gap-2">
                    See the Subscription <ArrowRight className="h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Outcomes Section */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
              What "good" looks like after BoardSight.
            </h2>
            <ul className="space-y-4 mb-8">
              {OUTCOMES.map((outcome, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="h-6 w-6 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-lg text-foreground">{outcome}</span>
                </li>
              ))}
            </ul>
            <p className="text-lg text-muted-foreground text-center italic">
              So your board can credibly answer: "How are we overseeing AI risk?"
            </p>
          </div>
        </div>
      </section>

      {/* Assessment CTA Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
              Start with a 5‑minute diagnostic.
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Benchmark your board's AI oversight readiness and identify the highest‑impact gaps to address next.
            </p>
            <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <Link to="/quiz">
                Begin the Assessment
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">
              Your data is used only to generate your results.
            </p>
          </div>
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-12 text-center">
            Designed for boardroom reality.
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {TESTIMONIALS.map((testimonial, index) => (
              <Card key={index} className="bg-card border-border">
                <CardContent className="pt-6">
                  <Quote className="h-8 w-8 text-primary/30 mb-4" />
                  <p className="text-foreground italic mb-4">
                    "{testimonial.quote}"
                  </p>
                  <p className="text-sm text-muted-foreground font-medium">
                    — {testimonial.attribution}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8 text-center">
              Frequently Asked Questions
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {HOMEPAGE_FAQS.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left text-foreground hover:text-primary">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 md:py-24">
        <div className="container-custom">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-8">
              Build confidence. Reduce risk. Strengthen oversight.
            </h2>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground">
                <Link to="/contact#briefing">
                  Book a Briefing
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
                <Link to="/blueprint#pricing">
                  Enroll in the Intensive
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Index;
